import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext'; // Import Auth Context
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faLocationArrow, faSignInAlt, faSignOutAlt, faGauge, faFingerprint, faUserShield, faScaleBalanced, faFileContract, faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import fflLogo from "../assets/ffl-logo.png";
import './Header.css';

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, logout, user } = useContext(AuthContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [islogout, setIslogout] = useState(false);

  useEffect(() => {
    // Listen for window resize to adjust the menu type
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (islogout) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    document.body.classList.toggle("modal-open", islogout);
  }, [islogout]);

  useEffect(() => {
    const open = isMobile && menuOpen;
    document.body.classList.toggle('mobile-menu-open', open);
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.classList.remove('mobile-menu-open');
      document.body.style.overflow = '';
    };
  }, [isMobile, menuOpen]);

  // (optional) close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const showLogoutModal = () => {
    toggleMenu();
    setIslogout(true);
  };

  const handleLogout = async () => {
    if (!window.dispatchEvent(new Event("ffls:before-editor-leave", { cancelable: true }))) return;
    setIslogout(false);
    await logout("manual");
    if (menuOpen) toggleMenu();
    navigate("/login", { replace: true });
  };

  // ✅ Close the delete modal
  const closeLogoutModal = () => {
    setIslogout(false);
  };

  return (
    <header className="app-header">
      <div onClick={() => navigate('/')} className="header-logo-container">
        <img src={fflLogo} alt="FFLs Logo" className="header-logo" />
      </div>

      {islogout && (
        <div className="modal-overlay">
          <div className="modal modal-logout" role="dialog" aria-modal="true" aria-labelledby="logout-title">
            <h3>
              <FontAwesomeIcon icon={faSignOutAlt} />&nbsp;Log out
            </h3>
            <p>Are you sure you want to log out?</p>
            <br />
            <div className="modal-buttons">
              <button
                type="button"
                onClick={closeLogoutModal}
                className="cancel-delete"
              >
                No, Cancel
              </button>
              <button
                type="button"
                onClick={handleLogout}
                className="confirm-delete"
              >
                Yes, Log out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Navigation Menu */}
      {!isMobile && (
        <nav className="desktop-nav">
          <Link to="/" className={location.pathname === "/" ? "active-link" : ""}>
            <FontAwesomeIcon icon={faLocationArrow} /> Search&nbsp;by&nbsp;Distance
          </Link>
          <Link to="/name-search" className={location.pathname === "/name-search" ? "active-link" : ""}>
            <FontAwesomeIcon icon={faSearch} /> Search&nbsp;by&nbsp;Name
          </Link>
          {isAuthenticated ? (
            <>
              <Link
                to="/dashboard"
                className={location.pathname === "/dashboard" ? "active-link" : ""}
              >
                <FontAwesomeIcon icon={faGauge} /> Dashboard
              </Link>
              <button onClick={showLogoutModal} className="logout-button">
                <FontAwesomeIcon icon={faSignOutAlt} /> Log&nbsp;out
              </button>
            </>
          ) : (
            <Link to="/login" className={location.pathname === "/login" ? "active-link" : ""}>
              <FontAwesomeIcon icon={faSignInAlt} /> Log&nbsp;in
            </Link>
          )}
        </nav>
      )}

      {/* Mobile Hamburger Menu */}
      {isMobile && (
        <>
          <button className={`hamburger ${menuOpen ? 'open' : ''}`} onClick={toggleMenu}>
            <div className="bar"></div>
            <div className="bar"></div>
            <div className="bar"></div>
          </button>

          {/* Backdrop (click to close) */}
          {isMobile && menuOpen && (
            <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
          )}

          {/* Dropdown Menu for Mobile */}
          <div className={`dropdown-menu ${menuOpen ? 'open' : ''}`}>
            <ul>
              <li>
                <Link to="/" className={location.pathname === "/" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faLocationArrow} />
                    <span>Search by Distance</span>
                  </span>
                </Link>
              </li>

              <li>
                <Link to="/name-search" className={location.pathname === "/name-search" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faSearch} />
                    <span>Search by Name</span>
                  </span>
                </Link>
              </li>

              {isAuthenticated ? (
                <>
                  <li>
                    <Link to="/dashboard" className={location.pathname === "/dashboard" ? "active-link" : ""} onClick={toggleMenu}>
                      <span className="nav-item">
                        <FontAwesomeIcon icon={faGauge} />
                        <span>My Dashboard</span>
                      </span>
                    </Link>
                  </li>

                  <li>
                    <button onClick={showLogoutModal} className="logout-button">
                      <span className="nav-item">
                        <FontAwesomeIcon icon={faSignOutAlt} />
                        <span>Log out</span>
                      </span>
                    </button>
                  </li>
                </>
              ) : (
                <li>
                  <Link to="/login" className={location.pathname === "/login" ? "active-link" : ""} onClick={toggleMenu}>
                    <span className="nav-item">
                      <FontAwesomeIcon icon={faSignInAlt} />
                      <span>Log in</span>
                    </span>
                  </Link>
                </li>
              )}

              <li className="nav-divider"></li>

              <li>
                <Link to="/about" className={location.pathname === "/about" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faCircleInfo} />
                    <span>About</span>
                  </span>
                </Link>
              </li>

              <li>
                <Link to="/terms" className={location.pathname === "/terms" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faFileContract} />
                    <span>Terms of Use</span>
                  </span>
                </Link>
              </li>

              <li>
                <Link to="/privacy" className={location.pathname === "/privacy" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faFingerprint} />
                    <span>Privacy Policy</span>
                  </span>
                </Link>
              </li>

              <li>
                <Link to="/legal" className={location.pathname === "/legal" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faScaleBalanced} />
                    <span>Legal</span>
                  </span>
                </Link>
              </li>

              <li>
                <Link to="/hosting-terms" className={location.pathname === "/hosting-terms" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faFileContract} />
                    <span>Website Hosting Subscription Terms</span>
                  </span>
                </Link>
              </li>

              <li>
                <Link to="/hosting-refunds" className={location.pathname === "/hosting-refunds" ? "active-link" : ""} onClick={toggleMenu}>
                  <span className="nav-item">
                    <FontAwesomeIcon icon={faFileContract} />
                    <span>Cancellation &amp; Refund Policy</span>
                  </span>
                </Link>
              </li>

            </ul>
          </div>
        </>
      )
      }
    </header >
  );
};

export default Header;
